import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Send, MapPin, Calendar, Clock, Users, Repeat, CalendarDays, Languages, ImagePlus } from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { formatDateNL, formatRecurringDays, getActivityDateLabel } from '../utils/dateFormat';
import { formatPrice } from '../utils/priceFormat';
import { isAggregatorOrganizer } from '../utils/organizerLink';
import { translateAudience } from '../utils/activityLabels';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useAuth } from '../contexts/AuthContext';
import { useActivityTranslation } from '../contexts/TranslationContext';
import ColorBlockPlaceholder from './ColorBlockPlaceholder';
import api from '../lib/api';
import { toast } from 'sonner';


const InstagramActivityCard = ({ activity, onUpdate, isPast = false }) => {
  const navigate = useNavigate();
  const { user, t, language, requireAuth } = useAuth();
  const tr = useActivityTranslation(activity);
  const displayTitle = (tr && tr.title) || activity.title;
  const displayDescription = (tr && tr.description) || activity.description;
  // Prefer the server-computed booleans (slim card response).
  const initialLiked = activity.is_liked_by_me ?? activity.likes?.includes(user?.id) ?? false;
  const initialJoined = activity.is_joined_by_me ?? activity.participants?.includes(user?.id) ?? false;
  const [isLiked, setIsLiked] = useState(initialLiked);
  const [likesCount, setLikesCount] = useState(activity.likes_count || 0);
  const [isJoined, setIsJoined] = useState(initialJoined);
  const [participantsCount, setParticipantsCount] = useState(activity.participants_count || 0);

  const isPga = activity.source === 'pga';
  const noOrganizerLink = isAggregatorOrganizer(activity);
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  const photoUrl = activity.image_url || activity.pga_photo_url;
  const showColorBlock = !photoUrl || imgError;

  // Card thumbnail strategie:
  // - PGA foto's: use de WordPress -768x512 variant (kleiner downloaden)
  // - Lokale activity-photos: vervang `xyz.jpg` door `xyz-thumb.jpg` (zie scripts/optimize_activity_photos.py)
  const thumbUrl = (() => {
    if (!photoUrl) return photoUrl;
    if (photoUrl.includes('positiefgezondalmere.nl')) {
      return photoUrl.replace(/-1024x683\.jpg$/, '-768x512.jpg');
    }
    if (photoUrl.startsWith('/images/activity-photos/') && !photoUrl.includes('-thumb')) {
      return photoUrl.replace(/\.(jpe?g|png)$/i, '-thumb.jpg');
    }
    return photoUrl;
  })();
  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth('Log in om te liken.')) return;
    try {
      const response = await api.post(`/activities/${activity.id}/like`);
      setIsLiked(response.data.liked);
      setLikesCount(prev => response.data.liked ? prev + 1 : prev - 1);
    } catch { toast.error('Kon niet liken'); }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!requireAuth('Log in om mee te doen.')) return;
    try {
      const response = await api.post(`/activities/${activity.id}/join`);
      setIsJoined(response.data.joined);
      setParticipantsCount(prev => response.data.joined ? prev + 1 : prev - 1);
      toast.success(response.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Kon niet meedoen'); }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/activity/${activity.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: activity.title, text: activity.description, url }); } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link gekopieerd');
    }
  };

  const handleComment = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/activity/${activity.id}#comments`);
  };

  const handlePoster = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/poster?activity=${activity.id}`);
  };

  return (
    <Card 
      className="overflow-hidden border border-gray-100 bg-white rounded-3xl shadow-soft hover:shadow-hover hover:-translate-y-0.5 transition-all duration-300"
      data-testid={`activity-card-${activity.id}`}
      data-activity-name={activity.title}
      data-activity-category={activity.activity_type}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        {isPga ? (
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar className="w-9 h-9 flex-shrink-0 bg-white border border-gray-100">
              {activity.organization_logo ? (
                <AvatarImage src={activity.organization_logo} alt={activity.organization_name || ''} className="object-contain p-0.5" />
              ) : (
                <AvatarImage src="/images/hoi-buur-icon-source.png" alt="Hoi Buur" className="object-contain p-1" />
              )}
              <AvatarFallback className="bg-hoi-blue/10 text-hoi-blue text-xs font-bold">
                {(activity.organization_name || activity.organizer_name || '?').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-heading font-semibold text-gray-900 text-sm truncate">{activity.organization_name || activity.organizer_name}</p>
              <p className="text-xs text-gray-400 truncate">{activity.location}</p>
            </div>
          </div>
        ) : noOrganizerLink ? (
          <div className="flex items-center gap-3 min-w-0 flex-1" data-testid="organizer-static">
            <Avatar className="w-9 h-9 flex-shrink-0 bg-white border border-gray-100">
              {activity.organizer_avatar ? (
                <AvatarImage src={activity.organizer_avatar} alt={activity.organizer_name || ''} />
              ) : activity.organization_logo ? (
                <AvatarImage src={activity.organization_logo} alt={activity.organization_name || ''} className="object-contain p-0.5" />
              ) : (
                <AvatarImage src="/images/hoi-buur-icon-source.png" alt="Hoi Buur" className="object-contain p-1" />
              )}
              <AvatarFallback className="bg-hoi-blue/10 text-hoi-blue text-xs font-bold">
                {(activity.organization_name || activity.organizer_name || '?').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-heading font-semibold text-gray-900 text-sm truncate">{activity.organization_name || activity.organizer_name}</p>
              <p className="text-xs text-gray-400">{activity.wijk}</p>
            </div>
          </div>
        ) : (
          <Link to={`/profile/${activity.organizer_id}`} className="flex items-center gap-3 min-w-0 flex-1">
            <Avatar className="w-9 h-9 flex-shrink-0 bg-white border border-gray-100">
              {activity.organizer_avatar ? (
                <AvatarImage src={activity.organizer_avatar} alt={activity.organizer_name || ''} />
              ) : activity.organization_logo ? (
                <AvatarImage src={activity.organization_logo} alt={activity.organization_name || ''} className="object-contain p-0.5" />
              ) : (
                <AvatarImage src="/images/hoi-buur-icon-source.png" alt="Hoi Buur" className="object-contain p-1" />
              )}
              <AvatarFallback className="bg-hoi-blue/10 text-hoi-blue text-xs font-bold">
                {(activity.organizer_name || '?').charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="font-heading font-semibold text-gray-900 text-sm truncate">{activity.organizer_name}</p>
              <p className="text-xs text-gray-400">{activity.wijk}</p>
            </div>
          </Link>
        )}
        {/* Status badge (only cancelled) */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {activity.status === 'cancelled' ? (
            <Badge className="bg-red-50 text-red-600 border border-red-200 font-medium text-xs">{t('detail.cancelled')}</Badge>
          ) : null}
        </div>
      </div>

      {/* Image / Color Block */}
      <Link to={`/activity/${activity.id}`}>
        <div className="relative mx-3 overflow-hidden">
          {showColorBlock ? (
            <ColorBlockPlaceholder activityType={activity.activity_type} />
          ) : (
            <>
              {!imgLoaded && (
                <div className="w-full aspect-[4/3] bg-gray-100 animate-pulse" />
              )}
              <img
                src={thumbUrl}
                alt={activity.title}
                className={`w-full aspect-[4/3] object-cover transition-all duration-500 hover:scale-105 ${imgLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
                style={{ transform: 'rotate(-2deg) scale(1.04)' }}
                loading="lazy"
                decoding="async"
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
              />
            </>
          )}
          {activity.status === 'pending' && (
            <div className="absolute top-3 left-3">
              <Badge variant="outline" className="bg-yellow-50/90 text-yellow-700 border-yellow-200 text-xs">{t('detail.pending_badge')}</Badge>
            </div>
          )}
        </div>
      </Link>

      {/* Content */}
      <div className="px-5 pt-4 pb-5 space-y-3">
        {/* Title */}
        <Link to={`/activity/${activity.id}`}>
          <h3 className="font-heading font-bold text-2xl md:text-3xl text-gray-900 leading-snug">{displayTitle}</h3>
        </Link>
        {tr && (
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400" data-testid="card-translated-label">
            <Languages className="w-3 h-3" />{t('translation.machine')}
          </span>
        )}

        {/* Meta: type + recurring + cost as subtle tags */}
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-gray-100 text-gray-600 font-normal text-xs border-0">
            {t(`type.${activity.activity_type}`)}
          </Badge>
          {activity.target_audience && activity.target_audience.toLowerCase() !== 'iedereen' && (
            <Badge className="bg-hoi-blue/10 text-hoi-blue font-medium text-xs border border-hoi-blue/20" data-testid="audience-badge">
              <Users className="w-3 h-3 mr-1" />
              {translateAudience(activity.target_audience, t)}
            </Badge>
          )}
          {activity.recurring && !activity.recurring_days?.length && !isPast && (
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <Repeat className="w-3 h-3" />
              {t(`pattern.${activity.recurring_pattern || 'weekly'}`)}
            </span>
          )}
          {activity.is_free !== false ? (
            <Badge className="bg-green-50 text-green-700 font-medium text-xs border border-green-200" data-testid="cost-badge-free">{t('activity.free')}</Badge>
          ) : (
            <Badge className="bg-amber-50 text-amber-700 font-medium text-xs border border-amber-200" data-testid="cost-badge-paid">{formatPrice(activity.price)}</Badge>
          )}
          {activity.recurring && activity.continues_during_holidays === false && (
            <Badge className="bg-orange-50 text-orange-600 font-normal text-xs border border-orange-200" data-testid="holiday-badge">{t('badge.not_holidays')}</Badge>
          )}
          {isPga && activity.pga_occurrence_count > 1 && (
            <Badge className="bg-blue-50 text-blue-600 font-normal text-xs border border-blue-200" data-testid="recurring-pga-badge">
              <CalendarDays className="w-3 h-3 mr-1" />
              {t('badge.recurs')}
            </Badge>
          )}
          {Array.isArray(activity.locations) && activity.locations.length > 0 && (
            <Badge className="bg-hoi-coral/10 text-hoi-coral font-medium text-xs border border-hoi-coral/30" data-testid="multi-location-badge">
              <MapPin className="w-3 h-3 mr-1" />
              {t('badge.continuous_program')} · {activity.locations.length + 1} {t('badge.locations')}
            </Badge>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
          {displayDescription}
        </p>

        {/* Date/Time Row */}
        <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap">
          {(activity.recurring || activity.recurring_pattern || (activity.recurring_days && activity.recurring_days.length > 0)) ? (
            <span className="flex items-center gap-1 text-hoi-blue font-medium">
              <Repeat className="w-3.5 h-3.5" />
              {getActivityDateLabel(activity, language)}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {formatDateNL(activity.date, language)}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {activity.time}{activity.end_time ? ` – ${activity.end_time}` : ''}
          </span>
          {!isPga && activity.max_participants && (
            <span className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              {participantsCount}/{activity.max_participants}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-50 gap-2">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={handleLike}
              className="flex items-center gap-1.5 transition-transform active:scale-90"
              data-testid={`like-btn-${activity.id}`}
              aria-label={`${t('card.like')}${likesCount > 0 ? ` (${likesCount})` : ''}`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-500 hover:text-red-500'}`} />
              <span className={`hidden sm:inline text-xs font-medium ${isLiked ? 'text-red-500' : 'text-gray-600'}`}>{t('card.like')}</span>
              {likesCount > 0 && <span className="text-xs text-gray-400">({likesCount})</span>}
            </button>
            <button
              onClick={handleComment}
              className="flex items-center gap-1.5 transition-transform active:scale-90"
              data-testid={`comment-btn-${activity.id}`}
              aria-label={`${t('card.comment')}${activity.comments_count > 0 ? ` (${activity.comments_count})` : ''}`}
            >
              <MessageCircle className="w-5 h-5 text-gray-500 hover:text-hoi-blue" />
              <span className="hidden sm:inline text-xs font-medium text-gray-600">{t('card.comment')}</span>
              {activity.comments_count > 0 && <span className="text-xs text-gray-400">({activity.comments_count})</span>}
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 transition-transform active:scale-90"
              data-testid={`share-btn-${activity.id}`}
              aria-label={t('card.share')}
            >
              <Send className="w-5 h-5 text-gray-500 hover:text-hoi-coral" />
              <span className="hidden sm:inline text-xs font-medium text-gray-600">{t('card.share')}</span>
            </button>
            <button
              onClick={handlePoster}
              className="flex items-center gap-1.5 transition-transform active:scale-90"
              data-testid={`poster-btn-${activity.id}`}
              aria-label={t('card.make_poster')}
            >
              <ImagePlus className="w-5 h-5 text-gray-500 hover:text-hoi-blue" />
              <span className="hidden sm:inline text-xs font-medium text-gray-600">{t('card.make_poster')}</span>
            </button>
          </div>
          
          {!isPast && (
            isPga ? (
              <Link to={`/activity/${activity.id}`}>
                <Button className="rounded-full font-heading font-semibold bg-hoi-coral hover:bg-hoi-coral/90 text-white px-4 py-1.5 text-sm h-auto" data-testid={`pga-signup-btn-${activity.id}`} data-activity-name={activity.title} data-activity-category={activity.activity_type}>
                  {t('activity.view')}
                </Button>
              </Link>
            ) : (
              <Button
                onClick={handleJoin}
                className={`rounded-full font-heading font-semibold px-4 py-1.5 text-sm h-auto ${isJoined ? 'bg-hoi-blue hover:bg-hoi-blue/90 text-white' : 'bg-hoi-coral hover:bg-hoi-coral/90 text-white'}`}
                data-testid={`join-btn-${activity.id}`}
                data-activity-name={activity.title}
                data-activity-category={activity.activity_type}
              >
                <Users className="w-3.5 h-3.5 mr-1" />
                {isJoined ? t('activity.leave') : t('activity.join')}
              </Button>
            )
          )}
        </div>
      </div>
    </Card>
  );
};

export default InstagramActivityCard;
